import React, {useMemo} from 'react';
import {EMPHASIS, StepCard, Text} from '@forecast-it/design-system';
import CustomScrollDiv from '../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import {FormattedMessage, useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import Styled from 'styled-components';
import {projectUrl} from '../../directApi';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import type {BaselineReadonlyProject} from './BaselineTypes';

const PageWrapper = Styled.div`
	padding: 0 32px;
`;

const DescriptionContainer = Styled.div`
	width: 485px;
`;

const StepCardsContainer = Styled.div`
	display: flex;
	flex-direction: row;
	align-items: stretch;
	box-sizing: border-box;
	gap: 12px;
	margin: 24px 0 10px;
`;

interface DeliverablesGuideSectionProps {
	project: BaselineReadonlyProject;
	useDeliverables: boolean;
	baselineLocked?: boolean;
	/** The project's currency: its rate card's, falling back to the company's. */
	currency?: string;
	/**
	 * Page furniture that belongs above this section but inside its scroll container, so the
	 * Baseline scrolls as one region rather than pinning a block above an independent one.
	 */
	topContent?: React.ReactNode;
}

const DeliverablesGuideSection: React.FC<DeliverablesGuideSectionProps> = ({
	project,
	useDeliverables,
	baselineLocked,
	currency,
	topContent,
}) => {
	const intl = useIntl();
	const history = useHistory();
	const {formatMessage} = intl;

	const navigateToBaselineEdit = () => {
		history.push(`${projectUrl(project?.companyProjectId, project?.customProjectId)}/baseline/edit`);
	};

	const phases = project?.phases?.edges || [];
	const deliverables = project?.deliverables?.edges || [];

	const isPhasesCompleted = phases.length > 0;
	const isDeliverablesCompleted = useDeliverables ? deliverables.length > 0 : true;

	const isHoursAndExpensesDisabled = useMemo(
		() => !isPhasesCompleted || !isDeliverablesCompleted,
		[phases.length, deliverables.length]
	);
	return (
		<CustomScrollDiv horizontalcontent>
			{topContent}
			<PageWrapper data-cy={'baseline-deliverables-guide'}>
				<DescriptionContainer>
					<Text size={'2'}>
						<FormattedMessage id={'deliverables.guide_section.description'} />
					</Text>
				</DescriptionContainer>
				<StepCardsContainer>
					<StepCard
						disabled={baselineLocked}
						completed={isPhasesCompleted}
						headerTitle={
							isPhasesCompleted ? formatMessage({id: 'common.done'}) : formatMessage({id: 'common.todo'})
						}
						headerIcon={isPhasesCompleted ? 'checkCircle' : undefined}
						title={formatMessage({id: 'deliverables.guide_section.phases.title'})}
						icon={'phase'}
						button={{
							label: formatMessage({id: 'deliverables.guide_section.phases.button'}),
							emphasis: isPhasesCompleted ? EMPHASIS.MEDIUM : EMPHASIS.HIGH,
							onClick: () =>
								showModal({
									type: MODAL_TYPE.PHASE_MANAGEMENT_MODAL,
									initialPhases: phases,
									project,
									editBaselineDates: !project.synchBaselineAndScopingDates,
								}),
						}}
					>
						<FormattedMessage id={'deliverables.guide_section.phases.description'} />
					</StepCard>

					{useDeliverables && (
						<StepCard
							disabled={baselineLocked}
							completed={isDeliverablesCompleted}
							headerTitle={
								isDeliverablesCompleted
									? formatMessage({id: 'common.done'})
									: formatMessage({id: 'common.todo'})
							}
							headerIcon={isDeliverablesCompleted ? 'checkCircle' : undefined}
							title={formatMessage({id: 'deliverables.guide_section.deliverables.title'})}
							icon={'deliverable'}
							button={{
								label: formatMessage({id: 'deliverables.guide_section.deliverables.button'}),
								emphasis: isDeliverablesCompleted ? EMPHASIS.MEDIUM : EMPHASIS.HIGH,
								onClick: () =>
									showModal({
										type: MODAL_TYPE.MANAGE_DELIVERABLES_MODAL,
										deliverables,
										projectId: project.id,
										budgetType: project.budgetType,
										currency,
									}),
							}}
						>
							<FormattedMessage id={'deliverables.guide_section.deliverables.description'} />
						</StepCard>
					)}

					<StepCard
						completed={false}
						disabled={isHoursAndExpensesDisabled || baselineLocked}
						headerTitle={
							isHoursAndExpensesDisabled
								? useDeliverables
									? formatMessage({
											id: 'deliverables.guide_section.hours_and_expenses.header_title',
									  })
									: formatMessage({
											id: 'deliverables.guide_section.hours_and_expenses_no_deliverables.header_title',
									  })
								: formatMessage({id: 'common.todo'})
						}
						headerIcon={isHoursAndExpensesDisabled ? 'lockClosed' : undefined}
						title={formatMessage({id: 'deliverables.guide_section.hours_and_expenses.title'})}
						icon={'timeEstimate'}
						button={{
							label: formatMessage({
								id: 'deliverables.guide_section.hours_and_expenses.button',
							}),
							emphasis: EMPHASIS.HIGH,
							onClick: () => navigateToBaselineEdit(),
						}}
					>
						<FormattedMessage id={'deliverables.guide_section.hours_and_expenses.description'} />
					</StepCard>
				</StepCardsContainer>
			</PageWrapper>
		</CustomScrollDiv>
	);
};

export default DeliverablesGuideSection;
